/* __next_internal_client_entry_do_not_use__ Wrapper,Modal auto */ import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ScrollStore } from "@faire/web--source/common/ScrollStore";
import { trackEvent } from "@faire/web--source/common/trackEvent";
import { ThemeProvider } from "@faire/web--source/slate/Theme/ThemeProvider";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { trackSlateDesignSystemUiModalShowView } from "@faire/web-api--source/events/slateDesignSystem/view/uiModalShow";
import React, { useCallback, useEffect, useRef } from "react";
import styled, { useTheme } from "styled-components";
import { BaseModal } from "./BaseModal";
import { ModalNavigationAbsolute } from "./ModalNavigation/ModalNavigationAbsolute";
import { ModalNavigationFixed } from "./ModalNavigation/ModalNavigationFixed";
/**
 * Default wrapping div for the children of the `Modal`.
 * Exported so that modifications can be made to this for
 * cases where smaller content can be centered by wrapping
 * it in a parent component.
 *
 * @trackcomponent
 */ export var Wrapper = styled.div.withConfig({
    componentId: "sc-246630f5-0"
})([
    "display:flex;flex-direction:column;align-items:",
    ";justify-content:",
    ";overflow:inherit;height:100%;width:100%;box-sizing:border-box;padding:",
    ";"
], function(param) {
    var $align = param.$align;
    return $align;
}, function(param) {
    var $justify = param.$justify;
    return $justify;
}, function(param) {
    var $padding = param.$padding;
    return $padding;
});
/**
 * @deprecated use Modal from slate/Modal
 * @deprecatedBy "@faire/web--source/slate/Modal/Modal#Modal"
 * @trackcomponent
 */ export var Modal = function(param) {
    var primary = param.primary, padding = param.padding, children = param.children, onClose = param.onClose, _param_closeIconProps = param.closeIconProps, closeIconProps = _param_closeIconProps === void 0 ? {} : _param_closeIconProps, _param_showCloseButton = param.showCloseButton, showCloseButton = _param_showCloseButton === void 0 ? true : _param_showCloseButton, onBack = param.onBack, _param_backIconProps = param.backIconProps, backIconProps = _param_backIconProps === void 0 ? {} : _param_backIconProps, onForward = param.onForward, _param_forwardIconProps = param.forwardIconProps, forwardIconProps = _param_forwardIconProps === void 0 ? {} : _param_forwardIconProps, _param_stickyNav = param.stickyNav, stickyNav = _param_stickyNav === void 0 ? false : _param_stickyNav, width = param.width, height = param.height, className = param.className, parentElement = param.parentElement, contentStyle = param.contentStyle, overlayStyle = param.overlayStyle, shouldReturnFocusAfterClose = param.shouldReturnFocusAfterClose, shouldCloseOnOverlayClick = param.shouldCloseOnOverlayClick, _param_justify = param.justify, justify = _param_justify === void 0 ? "flex-start" : _param_justify, _param_align = param.align, align = _param_align === void 0 ? "center" : _param_align, ariaHideApp = param.ariaHideApp, dataTestId = param["data-test-id"], setContentRef = param.setContentRef, postEventModalId = param.postEventModalId, _param_isOpen = param.isOpen, isOpen = _param_isOpen === void 0 ? true : _param_isOpen, allowBackgroundScroll = param.allowBackgroundScroll, noPortal = param.noPortal, ariaLabelledBy = param["aria-labelledby"], ariaDescribedBy = param["aria-describedby"];
    var postedEvent = useRef(false);
    var shouldPopScrollStore = useRef(false);
    var postShowModalEvent = useCallback(function() {
        postedEvent.current = true;
        if (postEventModalId) {
            trackEvent("/ui/modal/show/".concat(postEventModalId));
            trackSlateDesignSystemUiModalShowView(postEventModalId);
        }
    }, [
        postEventModalId
    ]);
    useEffect(function() {
        if (isOpen && !postedEvent.current) {
            postShowModalEvent();
        }
    }, [
        isOpen,
        postShowModalEvent
    ]);
    useEffect(function() {}, []);
    var handleAfterOpen = function() {
        if (!allowBackgroundScroll) {
            ScrollStore.get().pushNoScroll();
            shouldPopScrollStore.current = true;
        }
    };
    var handleAfterClose = function() {
        maybePopScrollStore();
    };
    var maybePopScrollStore = function() {
        if (shouldPopScrollStore.current) {
            ScrollStore.get().popNoScroll();
            shouldPopScrollStore.current = false;
        }
    };
    var handleWrapperClick = function(e) {
        // React synthetic events bubble through portals.
        // stopPropagation() will stop click events from bubbling up to the parent component.
        e.stopPropagation();
    };
    var viewport = useViewport();
    var theme = useTheme();
    var nestedTheme = _object_spread_props(_object_spread({}, theme), {
        buttons: theme.buttons
    });
    var navProps = {
        onClose: onClose,
        onBack: onBack,
        onForward: onForward,
        primary: primary,
        showCloseButton: showCloseButton,
        closeIconProps: closeIconProps,
        backIconProps: backIconProps,
        forwardIconProps: forwardIconProps
    };
    return /*#__PURE__*/ _jsx(ThemeProvider, {
        theme: nestedTheme,
        children: /*#__PURE__*/ _jsx(BaseModal, {
            contentRef: setContentRef,
            ariaHideApp: ariaHideApp,
            primary: primary,
            className: className,
            isOpen: isOpen,
            shouldReturnFocusAfterClose: shouldReturnFocusAfterClose,
            onAfterClose: handleAfterClose,
            onAfterOpen: handleAfterOpen,
            parentSelector: parentElement === undefined ? undefined : function() {
                return parentElement;
            },
            style: {
                content: _object_spread({
                    maxHeight: "100vh",
                    height: "".concat(viewport.isMobile ? "100%" : height !== null && height !== void 0 ? height : "600px"),
                    width: "".concat(viewport.isMobile ? "100%" : width !== null && width !== void 0 ? width : "500px")
                }, contentStyle !== null && contentStyle !== void 0 ? contentStyle : {}),
                overlay: _object_spread({}, noPortal ? {
                    position: "fixed",
                    width: "100vw",
                    height: "100vh",
                    top: 0,
                    left: 0
                } : {}, overlayStyle !== null && overlayStyle !== void 0 ? overlayStyle : {})
            },
            onRequestClose: onClose,
            "data-test-id": "faireModal",
            shouldCloseOnOverlayClick: shouldCloseOnOverlayClick,
            noPortal: noPortal,
            aria: {
                labelledby: ariaLabelledBy,
                describedby: ariaDescribedBy
            },
            children: /*#__PURE__*/ _jsxs(Wrapper, {
                onClick: handleWrapperClick,
                "data-test-id": dataTestId,
                $padding: padding !== null && padding !== void 0 ? padding : viewport.isMobile ? "64px 12px" : "30px 57px",
                $justify: justify,
                $align: align,
                children: [
                    stickyNav ? /*#__PURE__*/ _jsx(ModalNavigationFixed, _object_spread({}, navProps)) : /*#__PURE__*/ _jsx(ModalNavigationAbsolute, _object_spread({}, navProps)),
                    children
                ]
            })
        })
    });
};
